import { createFileRoute, Link, Outlet, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { Menu, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/hooks/useAuth";
import { adminAmI } from "@/lib/admin.functions";
import { privatePageMetadata } from "@/lib/seo";

export const Route = createFileRoute("/nc/live/adm")({
  head: () =>
    privatePageMetadata("/nc/live/adm", "Control room | BeHot.Now", "Internal administration."),
  component: AdminLayout,
});

const GROUPS = [
  {
    label: "Overview",
    items: [
      { to: "/nc/live/adm", label: "Dashboard", exact: true },
      { to: "/nc/live/adm/analytics", label: "Analytics" },
      { to: "/nc/live/adm/health", label: "System health" },
    ],
  },
  {
    label: "People",
    items: [
      { to: "/nc/live/adm/users", label: "Users" },
      { to: "/nc/live/adm/access", label: "Roles & groups" },
      { to: "/nc/live/adm/segments", label: "Segments" },
      { to: "/nc/live/adm/support", label: "Support inbox" },
    ],
  },
  {
    label: "Content",
    items: [
      { to: "/nc/live/adm/listings", label: "Listings" },
      { to: "/nc/live/adm/boards", label: "Boards & boosts" },
      { to: "/nc/live/adm/services", label: "Services" },
      { to: "/nc/live/adm/history", label: "Board history" },
    ],
  },
  {
    label: "Commerce",
    items: [
      { to: "/nc/live/adm/marketplace", label: "Marketplace" },
      { to: "/nc/live/adm/mp", label: "Marketplace ops" },
      { to: "/nc/live/adm/finance", label: "Finance" },
      { to: "/nc/live/adm/payments", label: "Payments & refunds" },
      { to: "/nc/live/adm/ledger", label: "Wallet ledger" },
      { to: "/nc/live/adm/coupons", label: "Coupons" },
    ],
  },
  {
    label: "Operations",
    items: [
      { to: "/nc/live/adm/notifications", label: "Notifications" },
      { to: "/nc/live/adm/logs", label: "Audit log" },
      { to: "/nc/live/adm/settings", label: "Settings" },
    ],
  },
] as const;


function AdminLayout() {
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const { user, loading } = useAuth();
  const amI = useServerFn(adminAmI);

  const { data, isLoading } = useQuery({
    queryKey: ["adm-gate", user?.id],
    enabled: Boolean(user),
    queryFn: () => amI({ data: undefined }),
  });

  useEffect(() => {
    if (!loading && !user) void navigate({ to: "/login" });
  }, [loading, user, navigate]);

  if (loading || !user || isLoading) {
    return <div className="mx-auto max-w-5xl px-4 py-20 text-muted-foreground">Loading…</div>;
  }

  if (!data?.isAdmin) {
    return (
      <div className="mx-auto max-w-3xl px-4 py-24 text-center">
        <ShieldCheck className="mx-auto size-8 text-muted-foreground" />
        <h1 className="mt-3 font-display text-2xl font-bold">Restricted</h1>
        <p className="mt-2 text-sm text-muted-foreground">This area is for BeHot.Now staff.</p>
        <Button asChild variant="hot" className="mt-6">
          <Link to="/">Back home</Link>
        </Button>
      </div>
    );
  }

  const nav = (
    <div className="space-y-5">
      {GROUPS.map((g) => (
        <div key={g.label}>
          <p className="px-3 pb-1 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
            {g.label}
          </p>
          <div className="flex flex-col gap-0.5">
            {g.items.map((t) => (
              <Link
                key={t.to}
                to={t.to}
                onClick={() => setMobileOpen(false)}
                activeOptions={{ exact: "exact" in t }}
                activeProps={{ className: "bg-primary/10 text-primary" }}
                className="rounded-lg px-3 py-2 text-sm font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
              >
                {t.label}
              </Link>
            ))}
          </div>
        </div>
      ))}
    </div>
  );

  return (
    <div className="mx-auto max-w-[100rem] px-4 py-8">
      <div className="flex flex-wrap items-center gap-3">
        <h1 className="font-display text-2xl font-bold">Control room</h1>
        <span className="rounded-full bg-primary/10 px-2 py-0.5 text-xs font-semibold uppercase tracking-wide text-primary">
          Admin
        </span>
        <Button
          variant="outline"
          size="sm"
          className="ml-auto lg:hidden"
          onClick={() => setMobileOpen((v) => !v)}
        >
          <Menu className="mr-1.5 size-4" />
          Menu
        </Button>
      </div>

      {mobileOpen ? (
        <nav className="mt-4 rounded-xl border border-border bg-card p-3 lg:hidden">{nav}</nav>
      ) : null}

      <div className="mt-6 gap-8 lg:flex">
        <aside className="hidden w-56 shrink-0 lg:block">
          <nav className="sticky top-20">{nav}</nav>
        </aside>
        <div className="min-w-0 flex-1">
          <Outlet />
        </div>
      </div>
    </div>
  );

}
